import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Check } from 'lucide-react';

/**
 * DesignPicker — reusable searchable grid of existing designs. Used in the
 * New Order dialog and Order Detail linking flow. Fully controlled: the
 * parent owns the selected ``value`` (design id) and receives updates via
 * ``onChange(designId, designObject)``.
 */
export function DesignPicker({ companyId, value, onChange, testIdPrefix = 'design-picker' }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!companyId) return undefined;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/designs/', {
          params: { company_id: companyId, q: q || undefined },
        });
        if (!cancel) setItems(data?.designs || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [companyId, q]);

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      <div className="relative mb-2">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search design no… (e.g. RD-101)"
          className="pl-7 h-8 text-[12px]"
          data-testid={`${testIdPrefix}-search`}
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400">
          <Loader2 className="animate-spin" size={18} />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-[12px] text-slate-500" data-testid={`${testIdPrefix}-empty`}>
          No designs found. Create a new one instead.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
          {items.map((d) => {
            const selected = value === d.id;
            return (
              <button
                type="button"
                key={d.id}
                onClick={() => onChange?.(d.id, d)}
                className={`relative rounded-lg border overflow-hidden bg-white text-left transition-[box-shadow,border-color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)] ${selected ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-[var(--divider)] hover:border-indigo-300'}`}
                data-testid={`${testIdPrefix}-item-${d.design_no}`}
              >
                <div className="aspect-square w-full bg-slate-50">
                  {d.image_thumb ? (
                    <img src={d.image_thumb} alt={d.design_no} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-[10px]">No image</div>
                  )}
                </div>
                {selected && (
                  <span className="absolute top-1 right-1 bg-indigo-600 text-white rounded-full p-0.5">
                    <Check size={12} />
                  </span>
                )}
                <div className="px-1.5 py-1 text-[11px] font-mono font-medium text-slate-700 truncate">{d.design_no}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DesignPicker;
